/**
 * Fully custom mega-menu controller. Deliberately independent of Horizon's own
 * <header-menu> component/header-menu.js - own open/close state, own tab switching,
 * own hover-intent handling. Built 2026-08-25 after repeated fragility grafting
 * custom tabbed behavior onto Horizon's pointer-tracking system.
 *
 * Structure expected in the DOM:
 *   <custom-mega-menu>
 *     <ul>
 *       <li class="cmm-item">
 *         <a class="cmm-toplink">...</a>
 *         <div class="cmm-panel">
 *           <div class="cmm-tab" data-cmm-tab>          <!-- one per producttype -->
 *             <a class="cmm-tab-link">...</a>
 *             <div class="cmm-tab-pane">...</div>
 *           </div>
 *         </div>
 *       </li>
 *     </ul>
 *     <div class="cmm-overlay"></div>
 *   </custom-mega-menu>
 */
(function () {
  const OPEN_DELAY = 80; // ms before opening on hover-intent
  const CLOSE_DELAY = 250; // ms grace period before closing, tolerates gaps/flicker

  class CustomMegaMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(':scope > ul > .cmm-item'));
      this.overlay = this.querySelector(':scope > .cmm-overlay');
      // Back to a direct child of <body> (2026-08-27) - this overlay now only needs
      // to dim ordinary page CONTENT (confirmed empirically: staying under
      // .header-section's z-index:8 keeps it off the whole header). The announcement
      // bar and logo row get their OWN separate, independently-controlled dimming
      // layers instead (::after pseudo-elements - see custom-mega-menu.css), rather
      // than trying to make this one overlay partially cover a shared stacking
      // context. Much easier to reason about: three independent, locally-scoped
      // layers instead of one overlay fighting for partial control.
      if (this.overlay && this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }
      this._openTimer = null;
      this._closeTimer = null;
      this._activeItem = null;

      this.items.forEach((item) => {
        const panel = item.querySelector(':scope > .cmm-panel');
        const topLink = item.querySelector(':scope > .cmm-toplink');
        if (!panel) return;

        item.addEventListener('mouseenter', () => this._scheduleOpen(item));
        item.addEventListener('focusin', () => this._openNow(item));
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) this._scheduleClose();
        });
        topLink?.addEventListener('click', (e) => {
          if (panel && window.matchMedia('(hover: none)').matches) {
            e.preventDefault();
            this._activeItem === item ? this._closeNow() : this._openNow(item);
          }
        });

        const tabs = Array.from(panel.querySelectorAll(':scope > .cmm-tab-links > [data-cmm-tab]'));
        tabs.forEach((tab, i) => {
          const tabLink = tab.querySelector(':scope > .cmm-tab-link');
          tabLink?.addEventListener('mouseenter', () => this._activateTab(panel, tab));
          tabLink?.addEventListener('focus', () => this._activateTab(panel, tab));
          // Only sets the active class here (2026-08-27), not the full measurement -
          // the panel is still display:none at connectedCallback time, so every
          // scrollHeight read would be 0. Sizing for this default tab happens once
          // the panel actually becomes visible - see _openNow.
          if (i === 0) tab.classList.add('cmm-tab--active');
        });
      });

      // Container-level close scheduling (2026-08-27), not per-item - moving the
      // mouse between adjacent top items (crossing .cmm-toplist's own `gap`) was
      // triggering a full close/reopen cycle because mouseleave used to be on each
      // .cmm-item individually. The container itself never actually loses the mouse
      // during that transition (the pointer stays within .cmm's own row even while
      // crossing the gap), so listening here eliminates the flicker by construction
      // instead of tuning delay timings. mouseenter here also defensively clears any
      // pending close - covers re-entering the menu from outside before the close
      // timer would have fired.
      this.addEventListener('mouseleave', () => this._scheduleClose());
      this.addEventListener('mouseenter', () => clearTimeout(this._closeTimer));

      this.overlay?.addEventListener('click', () => this._closeNow());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeNow();
      });
      document.addEventListener('click', (e) => {
        if (this._activeItem && !this._activeItem.contains(e.target)) this._closeNow();
      });
    }

    _activateTab(panel, activeTab) {
      panel.querySelectorAll(':scope > .cmm-tab-links > [data-cmm-tab]').forEach((tab) => {
        tab.classList.toggle('cmm-tab--active', tab === activeTab);
      });
      this._sizePane(panel, activeTab);
    }

    // Extracted from _activateTab (2026-08-27) - also called from _openNow for
    // whichever tab is active by default. A tab's real height can only be measured
    // once its panel is actually display:block; calling this only from _activateTab
    // meant the very first (default, i===0) tab's measurement always ran while the
    // panel was still display:none, reading 0 for everything - so it could never
    // correctly get .cmm-pane-list--split even when its content genuinely needed it.
    _sizePane(panel, activeTab) {
      // The tab-links column (2nd level) sets the panel's height. The pane's own
      // .cmm-pane-list (3rd level) starts in plain single-column flow (see
      // custom-mega-menu.css) - only switch it to 2 columns (.cmm-pane-list--split)
      // if, measured in that single-column state, it's actually taller than the
      // tab-links column. Doing this as a real DOM measurement rather than a CSS
      // column-fill/height trick (tried twice before, both unreliable at the moment
      // of layout) is deterministic: a short list always stays 1 column, a long one
      // only splits when it genuinely doesn't fit. The pane/divider are themselves
      // position:absolute (removed from normal flow) so they can't influence the
      // panel's own height on their own - panel height is set directly from the
      // tab-links column's measured height.
      requestAnimationFrame(() => {
        const linksBox = panel.querySelector(':scope > .cmm-tab-links');
        // Floor of 250px (2026-08-27) - some tab-links columns are very short (e.g.
        // Thema has just "Feestdagen"/"Seizoen", 2 rows), so comparing pane content
        // directly against that tiny height forced a split even for a modest 4-item
        // pane. The floor gives every pane reasonable room before splitting is
        // considered, while a genuinely tall tab-links column (most domains) still
        // sets the real height as before.
        const linksHeight = Math.max(linksBox ? linksBox.scrollHeight : 0, 250);
        const list = activeTab.querySelector(':scope > .cmm-tab-pane > .cmm-pane-list');
        if (list) {
          list.classList.remove('cmm-pane-list--split');
          if (list.scrollHeight > linksHeight) {
            list.classList.add('cmm-pane-list--split');
          }
        }
        panel.style.minHeight = linksHeight + 'px';
      });
    }

    _scheduleOpen(item) {
      clearTimeout(this._closeTimer);
      clearTimeout(this._openTimer);
      this._openTimer = setTimeout(() => this._openNow(item), OPEN_DELAY);
    }

    _openNow(item) {
      clearTimeout(this._closeTimer);
      clearTimeout(this._openTimer);
      // Defensively close every other item, not just the last-tracked one - guards
      // against any open panel ending up stuck regardless of event timing.
      this.items.forEach((other) => {
        if (other !== item) other.classList.remove('cmm-item--open');
      });
      item.classList.add('cmm-item--open');
      this._activeItem = item;
      this.classList.add('cmm-menu--open');
      this.overlay?.classList.add('cmm-overlay--visible');

      // Re-size whichever tab is currently active now that the panel is actually
      // display:block (2026-08-27) - see _sizePane's comment for why this can't only
      // happen in _activateTab.
      const panel = item.querySelector(':scope > .cmm-panel');
      const activeTab = panel?.querySelector(':scope > .cmm-tab-links > .cmm-tab--active');
      if (panel && activeTab) this._sizePane(panel, activeTab);
    }

    _scheduleClose() {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => this._closeNow(), CLOSE_DELAY);
    }

    _closeNow() {
      clearTimeout(this._openTimer);
      clearTimeout(this._closeTimer);
      if (this._activeItem) this._activeItem.classList.remove('cmm-item--open');
      this._activeItem = null;
      this.classList.remove('cmm-menu--open');
      this.overlay?.classList.remove('cmm-overlay--visible');
    }
  }

  if (!customElements.get('custom-mega-menu')) {
    customElements.define('custom-mega-menu', CustomMegaMenu);
  }
})();
